import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ApiResponse, PaginatedResponse } from '../../models/api.model';
import { ITextBlock, TextBlockType } from '../../models/text-block.model';
import { TextBlocksService } from '../../services/text-blocks.service';


@Component({
  selector: 'app-paste-text-block-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './paste-text-block-modal.component.html',
  styleUrls: ['./paste-text-block-modal.component.scss']
})
export class PasteTextBlockModalComponent implements OnInit, OnDestroy {
  public textBlocks: ITextBlock[] = [];
  public selectedTextBlock: ITextBlock | null = null;
  public isLoading = true;
  public searchTerm = '';

  public currentPage = 1;
  public pageSize = 100;
  public totalItems = 0;
  public totalPages = 1;

  private subscriptions = new Subscription();
  private searchTerms = new Subject<string>();
  private readonly type?: TextBlockType;

  constructor(
    @Inject('MODAL_CLOSE') private modalClose: (result: string | null) => void,
    @Inject('MODAL_DATA') data: { type?: TextBlockType } | null,
    private textBlocksService: TextBlocksService
  ) {
    this.type = data?.type;
  }

  ngOnInit(): void {
    this.loadTextBlocks();

    // Setup debounced search
    this.subscriptions.add(
      this.searchTerms.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(() => {
        // Reset to first page on search
        this.currentPage = 1;
        this.loadTextBlocks();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadTextBlocks(): void {
    this.isLoading = true;
    this.subscriptions.add(
      this.textBlocksService.getAllTextBlocks(
        this.searchTerm,
        'createdAt',
        'desc',
        this.currentPage,
        this.pageSize,
        this.type
      ).subscribe({
        next: (response: ApiResponse<PaginatedResponse<ITextBlock>>) => {
          const paginatedData = response.DATA;
          this.textBlocks = paginatedData.data || [];
          this.totalItems = paginatedData.total || 0;
          this.totalPages = paginatedData.totalPages || 1;
          this.isLoading = false;
        },
        error: (error: Error) => {
          console.error('Error loading text blocks:', error);
          this.textBlocks = [];
          this.totalItems = 0;
          this.totalPages = 1;
          this.isLoading = false;
        }
      })
    );
  }

  public selectTextBlock(textBlock: ITextBlock): void {
    this.selectedTextBlock = textBlock;
  }

  public addTextBlock(): void {
    if (this.selectedTextBlock) {
      this.modalClose(this.selectedTextBlock.description);
    }
  }

  public close(): void {
    this.modalClose(null);
  }

  public onSearchChange(): void {
    this.searchTerms.next(this.searchTerm);
  }

  public clearSearch(): void {
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadTextBlocks();
  }

  public onPageChange(page: number): void {
    this.currentPage = page;
    this.loadTextBlocks();
  }
}

